import type { PhotoGalleryCustomerData } from "@contracts/photosPageData";
import type { LocationData } from "@contracts/socialLocations";
import type { FirstPostsRequest } from "@contracts/contentCalendar";
import { isUploadableLocation } from "./LocationUploadRows.ts";

// Options for the "Create your first posts" modal ============================
// After the upload step, the modal (FirstPostsModal.vue) asks which of the customer's places, events, and albums the first
// posts should focus on. Each rule below prevents a specific problem:
//  1. Only options with photos. A place with no photos can't become a post, so offering it would promise something we can't deliver.
//  2. One option per live Location or Event, counting the unique photos across all of its albums. A place's second album would
//     otherwise show up as a duplicate of the place.
//  3. Albums not assigned to a live Location or Event are their own options. An album pointing at an archived Location or a Person
//     still counts as a plain album (same as rule 2 in LocationUploadRows.ts), so its photos can still be picked.
//  4. Order: A to Z by display name across places, events, and albums, so the list is easy to scan.
// The server turns the picked ids into the guidance for the topic gen (backend FirstPostsGuidance.cs), so names here are only for display.

/** Same limit as the backend (FirstPostsGuidance.MaxNoteLength). */
export const FIRST_POSTS_NOTE_MAX_LENGTH = 1000;

export type FocusOptionKind = "location" | "event" | "album";

export interface FocusOption {
  /** "loc:{locationId}" or "gal:{galleryId}". */
  key: string;
  kind: FocusOptionKind;
  /** Set for location and event options. */
  location: LocationData | null;
  /** Set for album options. */
  galleryId: string | null;
  title: string;
  photoCount: number;
}

function normalizeId(id?: string | null): string {
  return id?.trim().toLowerCase() ?? "";
}

function compareOptions(a: FocusOption, b: FocusOption): number {
  return a.title.localeCompare(b.title, undefined, { sensitivity: "base" }) || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
}

export function buildFocusOptions(galleries: PhotoGalleryCustomerData[] | null | undefined, locations: LocationData[] | null | undefined): FocusOption[] {
  const locationsById = new Map<string, LocationData>();
  for (const location of locations ?? []) {
    if (isUploadableLocation(location)) locationsById.set(normalizeId(location.id), location);
  }

  const photosByLocationId = new Map<string, Set<string>>();
  const options: FocusOption[] = [];
  for (const gallery of galleries ?? []) {
    if (!gallery?.id || gallery.deletedDate) continue;
    const photoIds = new Set((gallery.photoIds ?? []).filter(Boolean));
    // Rule 1
    if (!photoIds.size) continue;

    const location = locationsById.get(normalizeId(gallery.locationId));
    if (location) {
      // Rule 2
      const locationKey = normalizeId(location.id);
      const photos = photosByLocationId.get(locationKey) ?? new Set<string>();
      photoIds.forEach(photoId => photos.add(photoId));
      photosByLocationId.set(locationKey, photos);
    } else {
      // Rule 3
      options.push({ key: `gal:${gallery.id}`, kind: "album", location: null, galleryId: gallery.id, title: gallery.groupName?.trim() || "Untitled album", photoCount: photoIds.size });
    }
  }

  for (const [locationKey, photos] of photosByLocationId) {
    const location = locationsById.get(locationKey);
    options.push({
      key: `loc:${location.id}`,
      kind: location.tagKind === "Event" ? "event" : "location",
      location,
      galleryId: null,
      title: location.name?.trim() || "Unnamed location",
      photoCount: photos.size,
    });
  }

  // Rule 4
  return options.sort(compareOptions);
}

/** The "Create my first posts" request for the picked option keys. Keys that aren't in the options are ignored. */
export function buildFirstPostsRequest(options: FocusOption[], pickedKeys: Iterable<string>, note: string | null | undefined): FirstPostsRequest {
  const picked = new Set(pickedKeys);
  const chosen = options.filter(option => picked.has(option.key));
  return {
    locationIds: chosen.filter(option => option.location).map(option => option.location.id),
    galleryIds: chosen.filter(option => option.galleryId).map(option => option.galleryId),
    note: note?.trim() || null,
  };
}
